import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Typography, Tabs, Tab, Button, Stack, Skeleton,
  IconButton, Menu, MenuItem, Divider, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { botsApi } from "../../api/bots.js";
import { BadgeChip, botStateToBadgeColor, botStateLabel } from "../../components/BadgeChip.js";
import { DataSourcesTab } from "./tabs/DataSourcesTab.js";
import { InvitesTab } from "./tabs/InvitesTab.js";
import { EmbedTab } from "./tabs/EmbedTab.js";
import { AnalyticsTab } from "./tabs/AnalyticsTab.js";
import { SettingsTab } from "./tabs/SettingsTab.js";
import { ConversationsTab } from "./tabs/ConversationsTab.js";
import { PlaygroundTab } from "./tabs/PlaygroundTab.js";
import { WorkflowTab } from "./tabs/WorkflowTab.js";
import type { Bot } from "@chatfab/shared";

const TABS = ["Analytics", "Data Sources", "Conversations", "Playground", "Workflow", "Invites", "Embed", "Settings"];

export function BotPage() {
  const { id } = useParams<{ id: string }>();
  const nav    = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bot, setBot]     = useState<Bot | null>(null);
  const [loading, setLoading] = useState(true);

  const tabSlug = searchParams.get("tab") ?? "";
  const tab = Math.max(0, TABS.findIndex(t => t.toLowerCase().replace(/\s+/g, "-") === tabSlug));

  const setTab = (index: number) => {
    const slug = TABS[index].toLowerCase().replace(/\s+/g, "-");
    setSearchParams({ tab: slug }, { replace: true });
  };
  const [anchor, setAnchor]   = useState<null | HTMLElement>(null);

  const load = () => {
    if (!id) return;
    setLoading(true);
    botsApi.get(id).then(setBot).catch(() => nav("/dashboard")).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  if (loading) return (
    <Box><Skeleton variant="text" width={240} height={40} /><Skeleton variant="rounded" height={48} sx={{ mt: 2 }} /></Box>
  );
  if (!bot) return <Alert severity="error">Bot not found.</Alert>;

  const handleState = async (state: string) => {
    setAnchor(null);
    await botsApi.setState(bot.bot_id, state);
    load();
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton size="small" onClick={() => nav("/dashboard/bots")}><ArrowBackIcon fontSize="small" /></IconButton>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h2">{bot.name}</Typography>
              <BadgeChip label={botStateLabel(bot.state)} color={botStateToBadgeColor(bot.state)} />
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.25}>/{bot.slug}</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" href={`/b/${bot.slug}`} target="_blank" rel="noopener">
            Preview
          </Button>
          <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
        </Stack>
      </Stack>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {bot.state === "active"  && <MenuItem onClick={() => handleState("paused")}>Pause bot</MenuItem>}
        {bot.state === "paused"  && <MenuItem onClick={() => handleState("active")}>Activate bot</MenuItem>}
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); handleState("deleted"); nav("/dashboard"); }} sx={{ color: "error.main" }}>
          Delete bot
        </MenuItem>
      </Menu>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {TABS.map(t => <Tab key={t} label={t} />)}
        </Tabs>
      </Box>

      {tab === 0 && <AnalyticsTab botId={bot.bot_id} />}
      {tab === 1 && <DataSourcesTab botId={bot.bot_id} onUpdate={load} />}
      {tab === 2 && <ConversationsTab botId={bot.bot_id} />}
      {/* Playground stays mounted so the WebSocket and message history survive tab switches */}
      <Box sx={{ display: tab === 3 ? "block" : "none" }}>
        <PlaygroundTab botId={bot.bot_id} bot={bot} />
      </Box>
      {tab === 4 && <WorkflowTab botId={bot.bot_id} />}
      {tab === 5 && <InvitesTab botId={bot.bot_id} inviteRequired={bot.invite_required} onUpdate={load} />}
      {tab === 6 && <EmbedTab botId={bot.bot_id} bot={bot} onUpdate={load} />}
      {tab === 7 && <SettingsTab bot={bot} onUpdate={load} />}
    </Box>
  );
}
